/**
 * The door into a holding.
 *
 * *"i took over a town how do i go into it to build"* · *"you can swap between
 * towns once you capture to build both they should be seperate"* · *"you can
 * have multiple towns to manage"*.
 *
 * `World` has been a single settlement since the day it was written, and the
 * campaign has always been *two* worlds — the realm and the capital — stepped
 * together by `stepGame`. Nothing about that shape says "two". This file is the
 * third: given the realm and a province the player holds, it stands up a whole
 * settlement world for that ground, built out of what the strategic map already
 * knows about the place.
 *
 * ## The fork, and which way it was settled
 *
 * Three designs were available and the numbers decide between them. `npm run
 * towncost` prints them: a settlement world is **27.5 MiB of typed array**
 * resident, **0.53 MiB of save text**, **~50 ms to generate** and **0.20 ms a
 * tick** once somebody lives in it.
 *
 *   1. **Every holding gets a world, eagerly.** Rejected on the memory and the
 *      save. A campaign in which the player takes twenty provinces would be
 *      550 MiB of typed array and a ten-megabyte save, and the browser backend
 *      keeps saves in `localStorage`, whose five-megabyte quota is counted in
 *      UTF-16 — so the third town would fail to write. Paying for twenty towns
 *      to give the player the two they wanted is the wrong trade in every
 *      direction at once.
 *   2. **One world resident, the rest stored dormant** and reloaded on entry.
 *      Rejected on the design rather than the numbers. `step.ts` states the
 *      promise in as many words: *walk out of your city and the masons keep
 *      working*. A town that stops while nobody is looking at it makes
 *      residency **observable**, and by this project's fourth rule a cache
 *      whose presence changes what happens is not a cache — it is state, to be
 *      hashed, saved and explained to the player. That is a much larger thing
 *      than it looks.
 *   3. **Generated on first entry, and kept.** Taken. A holding nobody has
 *      walked into has no world and behaves exactly as every province on the
 *      board behaves today, which is to say this change costs nothing at all
 *      until the player asks for it. The moment they walk in, the place becomes
 *      a real world that ticks alongside the realm and the capital for the rest
 *      of the campaign. The bill is bounded by the number of towns the player
 *      actually chose to run, which is the only bill anybody can defend.
 *
 * **What would change my mind:** if a town has to exist before anybody has
 * stood in it — if, say, an assault on a holding the player has never entered
 * must be fought on that town's real ground rather than resolved. Today it is
 * resolved, exactly as it is for every province, so the rule is unchanged
 * rather than newly compromised. The other trigger is the browser wall: a
 * player with four towns is over `localStorage`'s quota, and the honest fix is
 * the desktop backend rather than a cleverer residency rule.
 *
 * ## What the town is made of
 *
 * Nothing invented. The province's own three numbers — its rung, its
 * fortification and whether it is somebody's seat — go to `raiseTown`, the same
 * generator the AI's towns are built by since the `aitowns` pass, so the town
 * you capture is recognisably the town you fought through. The ground under it
 * is generated for the country it stands in, through `WorldConfig.biome`, which
 * `cityart` built for exactly this. And the crown's knowledge travels with the
 * player: your Age, your tech, your relics and your unlocked buildings are
 * yours wherever you are standing.
 */

import { Team, Terrain } from '../types.ts';
import { createWorld, initRun, recomputeManaCap, recomputePopCap, recomputeStorage, spawnUnit, type World, type WorldConfig } from '../world.ts';
import { VILLAGER_TYPE } from '../../data/units.ts';
import { raiseTown, townRadius } from '../systems/aitown.ts';
import { recount } from '../systems/towers.ts';
import { provinceDef } from '../../data/provinces.ts';
import { deriveSeed, makeRng } from '../../core/rng.ts';
import { nationFactionId } from './armies.ts';
import { nationCapital } from '../../data/nations.ts';
import { LogTone, pushLog } from '../events.ts';
import type { CampaignState } from './types.ts';
import { isVillageHolding, villageLevel, villageTypeOf } from './villages.ts';
import { applyVillagePreset, siteVillageOnBank } from './villageworld.ts';

/**
 * The salt a holding's map seed is derived with.
 *
 * A hold's world is generated from the *campaign's* seed and its province
 * index, never from a clock or a counter, so two players in one room who walk
 * into the same holding walk into the same town, and a save reloaded a year
 * later rebuilds the ground it was built on. The number is arbitrary and the
 * only thing that matters about it is that it is not one of the salts already
 * spoken for in `createWorld` — 1, 31, 613, 977, 4441, 7717.
 */
export const HOLD_SEED_SALT = 90_001;

export function holdSeed(campaignSeed: number, province: number): number {
  return deriveSeed(campaignSeed, HOLD_SEED_SALT + province);
}

/**
 * Can the player walk into this ground, and if not, why not — as a finished
 * sentence, for a tooltip.
 *
 * Empty string means yes. Phrased as a refusal rather than a boolean for the
 * reason `fortifyBlock` is: a button that is greyed out and says nothing is a
 * bug report.
 */
export function holdBlock(realm: World, province: number): string {
  const c = realm.campaign;
  if (!c) return 'There is no realm here.';
  const prov = c.provinces[province];
  if (!prov) return 'There is no such province.';
  if (prov.owner !== c.playerNation) return 'This ground is not yours.';
  if (prov.siegeBy >= 0) return 'There is an army camped outside the walls.';
  return '';
}

/** Is there already a settlement world standing on this ground? */
export function isSettled(c: CampaignState, province: number): boolean {
  return (c.provinces[province]?.settled ?? 0) > 0;
}

/** Every holding of the player's that is a town, in province order. */
export function settledHoldings(c: CampaignState): number[] {
  const out: number[] = [];
  for (let p = 0; p < c.provinces.length; p++) {
    if (c.provinces[p].settled > 0 && c.provinces[p].owner === c.playerNation) out.push(p);
  }
  return out;
}

/**
 * The recipe for a holding's settlement world.
 *
 * Deliberately the same shape as `battleConfigFor`: a pure read of the realm
 * that returns something `createWorld` can be handed, so the decision about
 * *what* to build lives in the simulation and only the *when* lives in
 * `main.ts`. Null when the player cannot enter this ground.
 */
export function holdConfigFor(realm: World, province: number): WorldConfig | null {
  const c = realm.campaign;
  if (!c || holdBlock(realm, province)) return null;
  return {
    ...realm.config,
    seed: holdSeed(realm.config.seed, province),
    // Every settlement in a campaign is a capital in the sense that matters
    // here: no build timer, no director, besieged only when a host actually
    // arrives. See `WorldConfig.homeCity`.
    homeCity: true,
    biome: provinceDef(province).terrain,
    province,
  };
}

/**
 * What the crown knows travels to every town the crown holds.
 *
 * The opposite call from the one `carryCharacterInto` makes, and deliberately
 * so. A *battle field* gets the character and not the realm, because letting a
 * capital's forty tech nodes onto a skirmish field would make battle mode a
 * readout of the city builder. A *holding of yours* is the other case
 * entirely: it is your crown's town, its masons are your masons, and a player
 * who has spent a campaign climbing to the fourth Age would rightly be furious
 * to walk into their own second city and be offered a woodcutter's hut.
 *
 * `modsBase` is copied whole rather than re-folded from the relic and tech
 * lists. Re-folding would mean running fifty `apply(world)` closures again and
 * hoping they are all idempotent, which is a promise nothing in `data/relics.ts`
 * makes; copying the bag is exact. `mods` is then `modsBase` with nothing worn,
 * which is precisely right — the character and his cart are in the capital, and
 * he is one man.
 */
export function inheritCrown(hold: World, capital: World): void {
  hold.age = capital.age;
  hold.researchingAge = 0;
  hold.ageResearch = 0;
  hold.techNodes = capital.techNodes.slice();
  hold.relics = capital.relics.slice();
  hold.unlockedBuildings = capital.unlockedBuildings.slice();
  hold.unitRanks = { ...capital.unitRanks };
  hold.unlockedPowers = capital.unlockedPowers.slice();
  hold.powerTiers = { ...capital.powerTiers };
  hold.powerCooldowns = {};
  // Cooldowns are a fact about the fight they were spent in, not about the
  // crown. A power held ready in one town and spent in another is one dial in
  // two places, and the player would learn to spend it in the town they are not
  // defending. Fresh here, always.
  for (const p of hold.unlockedPowers) hold.powerCooldowns[p] = 0;
  hold.modsBase = { ...capital.modsBase };
  hold.mods = { ...capital.modsBase };
  // The calendar is the realm's, and there is one of it. A town that opened on
  // day zero while the capital was four years in would harvest out of step with
  // the map it stands on — the season multiplies every field's yield.
  hold.calendarDay = capital.calendarDay;
  hold.calendarT = capital.calendarT;
}

/**
 * Stand a whole settlement up on a holding the player owns.
 *
 * `capital` is the world the crown's knowledge is copied from — normally the
 * player's own capital. Passing null is legal and gives a town that knows only
 * what a fresh run knows, which is what a harness wants.
 */
export function createHoldWorld(realm: World, province: number, capital: World | null): World | null {
  const c = realm.campaign;
  const cfg = holdConfigFor(realm, province);
  if (!c || !cfg) return null;
  return buildProvinceTown(realm, province, cfg, capital, province === c.capital);
}

/** The same town before conquest: its province seed, native roofs and actual ground. */
export function createSiegeTownWorld(realm: World, province: number, crown: World | null = null): World | null {
  const c = realm.campaign;
  const prov = c?.provinces[province];
  if (!c || !prov) return null;
  const own = prov.owner === c.playerNation;
  const factionId = prov.owner >= 0 ? nationFactionId(c, prov.owner) : 'ironfang';
  const capital = own
    ? province === c.capital
    : province === nationCapital(c.nations[prov.owner]?.def ?? 0);
  return buildProvinceTown(realm, province, {
    ...realm.config,
    seed: holdSeed(realm.config.seed, province),
    factionId,
    raceId: own ? realm.config.raceId : undefined,
    leaderId: own ? realm.config.leaderId : undefined,
    homeCity: true,
    biome: provinceDef(province).terrain,
    province,
  }, own ? crown : null, capital);
}

function buildProvinceTown(
  realm: World, province: number, cfg: WorldConfig, capital: World | null, isCapital: boolean,
): World {
  const c = realm.campaign!;
  const prov = c.provinces[province];

  const village = isVillageHolding(c, province);
  const w = createWorld({ ...cfg, villageType: village ? villageTypeOf(c, province) : '' });
  initRun(w);
  // Some seeds put the founding party on a thin river spit. Before this fresh
  // holding has any player construction, use the village's broad-bank siting
  // routine for those city sites too; the whole Hearth and its residents move
  // together. A played town never reaches this generation path.
  let narrowBank = false;
  for (let y = w.map.hearthY - 7; y <= w.map.hearthY + 7 && !narrowBank; y++) {
    for (let x = w.map.hearthX - 7; x <= w.map.hearthX + 7; x++) {
      if (!w.map.inBounds(x,y)) continue;
      const t = w.map.terrain[w.map.idx(x,y)];
      if (t === Terrain.River || t === Terrain.Deep || t === Terrain.Bridge) { narrowBank = true; break; }
    }
  }
  if(village || narrowBank) siteVillageOnBank(w);
  if (capital && capital !== w) inheritCrown(w, capital);
  if (village) {
    applyVillagePreset(w, villageTypeOf(c, province), villageLevel(c, province), prov.fort);
    w.villageLastTurn=c.turn;
    pushLog(w.events, `${provinceDef(province).name} — a level ${w.villageLevel} village, supplied by its own countryside.`, LogTone.Lore);
    return w;
  }

  /**
   * Which rung the realm calls this place, and therefore which catalogue its
   * masons will take work from.
   *
   * The seat is 0 — "not a holding", the whole catalogue — rather than its own
   * rung, because a capital is a capital whatever the strategic map thinks its
   * development is, and a player whose seat was sacked down to rung three must
   * not walk into it and find that they may no longer raise a barracks in their
   * own city. Everywhere else takes the province's rung and a hamlet is a
   * hamlet. See `isVillage` and `promoteVillages` in `commonwealth.ts`; the
   * player can build their way out of it, which is what makes this a stage
   * rather than a verdict.
   */
  w.rung = province === c.capital ? 0 : Math.max(1, prov.tier);

  /**
   * The town that is already standing here.
   *
   * A holding is not empty ground: the strategic map has been calling it a
   * Market Town for eleven months and the player has quite possibly fought
   * through it. `raiseTown` is what built the one they fought through, so it is
   * what stands here — same catalogue, same streets, same four gates, at this
   * province's own rung and fortification.
   *
   * `Team.Player` rather than the previous owner's colours, and `w.wallTeam` is
   * already `Team.Player`: the ground is yours the moment you walk in. The
   * fight for it happened on the map.
   */
  const rng = makeRng(deriveSeed(cfg.seed, 17));
  const report = raiseTown(w, rng, {
    cx: w.map.hearthX,
    cy: w.map.hearthY,
    team: Team.Player,
    tier: Math.max(1, prov.tier),
    fort: prov.fort,
    capital: isCapital,
    raceId: w.raceId,
  });

  /**
   * And the people who live in it.
   *
   * `initRun` seeds twelve, which is a founding party rather than a town. A
   * holding on the fifth rung with forty roofs and twelve souls in it reads as
   * a ghost town and, worse, cannot work: the jobs pass has nobody to give the
   * buildings to, so the place produces nothing and the player concludes the
   * second town is broken. Scaled off the rung, and then clamped to the beds
   * that are actually standing — a town cannot hold more people than it has
   * houses for, and `recomputePopCap` has just counted them.
   */
  recomputePopCap(w);
  recomputeStorage(w);
  recomputeManaCap(w);
  const want = Math.min(w.popCap, 12 + Math.max(1, prov.tier) * 8);
  const R = townRadius(Math.max(1, prov.tier), isCapital);
  const residentPlots: number[] = [];
  const occupied = new Set<number>();
  for (let k = 0; k < report.interior.length; k++) {
    if (report.interior[k] && !w.map.isImpassable(k % w.map.w, Math.floor(k / w.map.w))) residentPlots.push(k);
  }
  for (let i = 0; i < w.units.slots.high; i++) if (w.units.isAlive(i)) {
    occupied.add(w.map.idx(Math.floor(w.units.x[i]), Math.floor(w.units.y[i])));
  }
  for (let i = w.pop; i < want; i++) {
    // Spread around the middle rather than piled on the Hearth: a hundred
    // villagers spawned on one tile spend their first ten seconds shoving each
    // other out of the way, which is the first thing the player would see.
    const ang = (i / Math.max(1, want)) * Math.PI * 2 * 3.7;
    const r = 2.5 + ((i * 7) % Math.max(3, R - 3));
    let x = w.map.hearthX + 0.5 + Math.cos(ang) * r;
    let y = w.map.hearthY + 0.5 + Math.sin(ang) * r;
    // A fitted bailey can be narrow on its river side. Keep the spreading
    // pattern, but put each newcomer on real open ground inside the curtain.
    let best = -1, score = Infinity;
    for (const k of residentPlots) {
      const d = (k % w.map.w + 0.5 - x) ** 2 + (Math.floor(k / w.map.w) + 0.5 - y) ** 2 + (occupied.has(k) ? 10000 : 0);
      if (d < score) { best = k; score = d; }
    }
    if (best >= 0) {
      x = best % w.map.w + 0.5; y = Math.floor(best / w.map.w) + 0.5;
      occupied.add(best);
    }
    spawnUnit(
      w,
      VILLAGER_TYPE,
      Team.Player,
      x,
      y,
    );
    w.pop++;
  }
  w.counts.villagers = w.pop;
  recount(w);
  w.dirty.jobs = true;
  w.dirty.districts = true;
  w.dirty.flow = true;

  pushLog(
    w.events,
    `${provinceDef(province).name} — ${report.buildings} roofs and ${report.wallTiles + report.gates} tiles of wall, and it is yours.`,
    LogTone.Lore,
  );
  return w;
}
